import * as i0 from '@angular/core';
import { Injectable, DEFAULT_CURRENCY_CODE, LOCALE_ID, Inject, Component, Input, ViewChild, ViewEncapsulation, NgModule } from '@angular/core';
import { __awaiter } from 'tslib';
import { getCompanyId } from '@onviobr/services/session';
import * as i1 from '@angular/common/http';
import { HttpParams, HttpClientModule } from '@angular/common/http';
import * as i8 from '@angular/common';
import { CurrencyPipe, getLocaleCurrencyCode, registerLocaleData, CommonModule } from '@angular/common';
import * as i1$1 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import * as wijmo from '@grapecity/wijmo';
import { CollectionView } from '@grapecity/wijmo';
import * as wjcGridXlsx from '@grapecity/wijmo.grid.xlsx';
import * as wjcGrid from '@grapecity/wijmo.grid';
import { saveAs } from 'file-saver';
import * as i4 from '@bento/bento-ng';
import { BentoModule } from '@bento/bento-ng';
import * as i5 from '@grapecity/wijmo.angular2.grid.grouppanel';
import { WjGridGrouppanelModule } from '@grapecity/wijmo.angular2.grid.grouppanel';
import * as i6 from '@grapecity/wijmo.angular2.grid';
import { WjGridModule } from '@grapecity/wijmo.angular2.grid';
import * as i9 from '@ng-bootstrap/ng-bootstrap';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import * as i10 from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BentoFlexGridControlModule } from '@bento/bento-ng-flexgrid-control';
import { cookie } from '@onviobr/services/utils';
import localEnGb from '@angular/common/locales/en-GB';
import localFrCa from '@angular/common/locales/fr-CA';

/**
 * Supported migration types
 */
var MigrationType;
(function (MigrationType) {
    MigrationType["TimeAndExpense"] = "time-and-expense-excel-import";
    MigrationType["AccountsReceivable"] = "accounts-receivable-excel-import";
    MigrationType["Clients"] = "client-excel-import";
})(MigrationType || (MigrationType = {}));

/**
 * Import statuses
 */
var Status;
(function (Status) {
    Status["Completed"] = "Completed";
    Status["InProgress"] = "In Progress";
    Status["Pending"] = "Pending";
    Status["Failed"] = "Failed";
})(Status || (Status = {}));

/**
 * Supported progress bar types
 */
var ProgressBar;
(function (ProgressBar) {
    ProgressBar["Success"] = "success";
    ProgressBar["Danger"] = "danger";
    ProgressBar["Info"] = "info";
})(ProgressBar || (ProgressBar = {}));

class MigrationService {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    /**
     * Retrieves migration events.
     * @param migrationType Migration type to filter on.
     * @param createdFrom Created from date to filter on
     */
    getMigrationEvents(migrationType, createdFrom) {
        return __awaiter(this, void 0, void 0, function* () {
            const params = new HttpParams().set('type', migrationType).set('createdFrom', createdFrom.toISOString());
            const response = this.httpClient.get(this.getGetUrl(), {
                params,
            });
            return response.toPromise();
        });
    }
    getGetUrl() {
        return '/api/migrations/v1/migration-events';
    }
}
MigrationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: MigrationService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
MigrationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: MigrationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: MigrationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }]; } });

class ContactService {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    getContactsByIds(contactIds, companyId) {
        return __awaiter(this, void 0, void 0, function* () {
            const query = {
                expand: 'displayAs',
                filterSearchSort: {
                    filter: '{"items":[{"by":"id","op":"IN","value":"' + contactIds.join() + '"}]}',
                },
            };
            const response = this.httpClient.post(this.getSearchUrl(companyId), query);
            return response.toPromise();
        });
    }
    getSearchUrl(companyId) {
        return '/api/core/v1/companies/' + companyId + '/contact-views/search';
    }
}
ContactService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ContactService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
ContactService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ContactService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ContactService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }]; } });

class ImportStatusService {
    constructor(migrationService, contactService) {
        this.migrationService = migrationService;
        this.contactService = contactService;
        /**
         * This is a store of the registry where all possible providers are stored with an Type
         */
        this.migrationsSummaryProviders = new Map();
        this.getCompanyId = getCompanyId;
    }
    /**
     * Adds a new summaryProvider to the registry and fails of the type is already used and the value is different
     * @param type - Type of the migration to register (must be unique)
     * @param provider - migration summary provider
     */
    addMigrationSummaryProvider(type, provider) {
        if (this.migrationsSummaryProviders.has(type) && this.migrationsSummaryProviders.get(type) !== provider) {
            throw new Error(`The migration ${type} is already registered with other value`);
        }
        this.migrationsSummaryProviders.set(type, provider);
    }
    /**
     * Returns summary provider for specific type
     * @param type Migartion type
     */
    getMigrationSummaryProvider(type) {
        this.assertMigrationType(type);
        return this.migrationsSummaryProviders.get(type);
    }
    /**
     * Returns imports statuses
     */
    getImportStatusesList(fromDate) {
        return __awaiter(this, void 0, void 0, function* () {
            const statuses = [];
            const migrations = yield this.getRegisteredMigrationsEvents(fromDate);
            const contactsData = yield this.getCreatorsContactData(this.getCreatorContactIds(migrations));
            migrations.forEach(migrationEvent => {
                const contactName = this.getContactNameById(contactsData, migrationEvent.creatorContactId);
                const statusItem = this.getImportStatus(migrationEvent, this.getEventStatusMessage(migrationEvent), contactName);
                if (statusItem) {
                    statuses.push(statusItem);
                }
            });
            // Sorts statuses by creation date
            const sortedByDate = statuses.sort((a, b) => b.date.valueOf() - a.date.valueOf());
            return sortedByDate;
        });
    }
    /**
     * Retrieves all migration event registered previously
     */
    getRegisteredMigrationsEvents(fromDate) {
        return __awaiter(this, void 0, void 0, function* () {
            let migrationEvents = [];
            const migrationEventsPromises = [];
            Array.from(this.migrationsSummaryProviders.keys()).forEach(value => {
                migrationEventsPromises.push(this.migrationService.getMigrationEvents(value, fromDate));
            });
            const migrationsPromise = Promise.all(migrationEventsPromises);
            migrationEvents = [].concat(...(yield migrationsPromise));
            return migrationEvents;
        });
    }
    /**
     * Extracts MigrationStatus from 'statusMessage' json string
     * @param migrationEvent Migartion event
     */
    getEventStatusMessage(migrationEvent) {
        const statusMessage = JSON.parse(migrationEvent.statusMessage);
        return statusMessage;
    }
    /**
     * Extracts ImportStatus data from migration event
     * @param migrationEvent Migration event
     * @param migrationEventStatusMessage Import status data
     */
    getImportStatus(migrationEvent, migrationEventStatusMessage, contactName) {
        const migrationType = migrationEvent.type;
        // ToDo: add enum value checking
        // if type defined - proceed parsing
        if (migrationType) {
            const summaryProvider = this.getMigrationSummaryProvider(migrationType);
            const migrationStatus = this.getStatus(migrationEvent.status);
            const isMigrationSuccessful = migrationEventStatusMessage.FailedRows === 0;
            const importStatus = {
                migrationEventId: migrationEvent.id,
                date: new Date(migrationEvent.created),
                status: migrationStatus,
                fileName: migrationEventStatusMessage.ImportedFileName,
                data: summaryProvider.dataType,
                uploadedBy: contactName,
                isSuccessful: isMigrationSuccessful,
                importProgress: this.getImportProgressCount(migrationStatus, migrationEventStatusMessage.ProcessedItems, migrationEventStatusMessage.TotalItems),
                progressBarType: this.getImportStatusProgressBarType(isMigrationSuccessful, migrationStatus),
                summary: this.getSummary(summaryProvider, migrationEventStatusMessage, migrationStatus),
                numberOfRowsImported: this.getImportedRowsNumber(migrationEventStatusMessage, migrationStatus),
                numberOfFailedRows: this.getFailedRowsNumber(migrationEventStatusMessage, migrationStatus),
            };
            return importStatus;
        }
    }
    /**
     * Returns current summary (depends on migration status)
     * @param summaryProvider Summary provider
     * @param migrationStatusMessage Status message
     * @param migrationStatus Migration status
     */
    getSummary(summaryProvider, migrationStatusMessage, migrationStatus) {
        if (migrationStatus === Status.Completed || migrationStatus === Status.Failed) {
            return summaryProvider.getSummary(migrationStatusMessage.Results, migrationStatusMessage);
        }
        return null;
    }
    /**
     * Returns actual number of imported rows for "Completed" status
     * @param migrationStatusMessage Migration status
     * @param migrationStatus migration status
     */
    getImportedRowsNumber(migrationStatusMessage, migrationStatus) {
        if (migrationStatus === Status.Completed || migrationStatus === Status.Failed) {
            return migrationStatusMessage.SuccessRows;
        }
        return null;
    }
    /**
     * Returns actual number of failed rows for "Completed" or "Failed" status
     * @param migrationStatusMessage Information about migration
     * @param migrationStatus migration status
     */
    getFailedRowsNumber(migrationStatusMessage, migrationStatus) {
        if (migrationStatus === Status.Completed || migrationStatus === Status.Failed) {
            return migrationStatusMessage.FailedRows;
        }
        return null;
    }
    /**
     * Returns import progress percentage
     * @param processedItems Numbers of rows processed
     * @param totalItems Total numbers of rows to process
     */
    getImportProgressCount(status, processedItems, totalItems) {
        if (status === Status.Completed || status === Status.Failed) {
            return 100;
        }
        if (status === Status.InProgress && processedItems && totalItems) {
            const progress = (processedItems * 100) / totalItems;
            return Math.floor(progress);
        }
        return 0;
    }
    /**
     * Returns Import status
     * @param migrationStatus Migration status
     */
    getStatus(migrationStatus) {
        switch (migrationStatus) {
            case 'pending':
                return Status.Pending;
            case 'retrieved':
                return Status.InProgress;
            case 'processed':
                return Status.Completed;
            case 'remoteError':
                return Status.Failed;
            default:
                break;
        }
    }
    /**
     * Returns contacts filtered by id
     * @param creatorContactIds Contact ids
     */
    getCreatorsContactData(creatorContactIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const companyId = yield this.getCompanyId();
            const contacts = yield this.contactService.getContactsByIds(creatorContactIds, companyId);
            return contacts.items;
        });
    }
    /**
     * Returns list of contacts ids
     * @param migrationEvents Migration events
     */
    getCreatorContactIds(migrationEvents) {
        const uniqueContactIds = [...new Set(migrationEvents.map(item => item.creatorContactId))];
        return uniqueContactIds;
    }
    /**
     * Returns contact name by id
     * @param contacts Contacts storage
     * @param creatorContactId Contact id
     */
    getContactNameById(contacts, creatorContactId) {
        const contact = contacts.find(contactView => contactView.id === creatorContactId);
        return contact.displayAs;
    }
    /**
     * Throws an error if migration type isn't registered
     * @param type - Type of the migration
     */
    assertMigrationType(type) {
        if (!this.migrationsSummaryProviders.has(type)) {
            throw new Error(`The provider ${type} is not registered`);
        }
    }
    /**
     * Returns progress bar type
     * @param isSuccessful - indicates whether import is successful or not
     * @param importStatus - status of the import
     */
    getImportStatusProgressBarType(isSuccessful, importStatus) {
        if (importStatus === Status.Completed && isSuccessful) {
            return ProgressBar.Success;
        }
        if ((importStatus === Status.Completed && !isSuccessful) || importStatus === Status.Failed) {
            return ProgressBar.Danger;
        }
        return ProgressBar.Info;
    }
}
ImportStatusService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportStatusService, deps: [{ token: MigrationService }, { token: ContactService }], target: i0.ɵɵFactoryTarget.Injectable });
ImportStatusService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportStatusService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportStatusService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: MigrationService }, { type: ContactService }]; } });

class WipSummaryProvider {
    constructor(currencyCode, locale, translateService) {
        this.translateService = translateService;
        this.currencyCode = currencyCode;
        this.locale = locale;
    }
    get dataTypeKey() {
        return 'bm.import.status.type.wip.title';
    }
    get summaryKey() {
        return 'bm.import.status.type.wip.summary';
    }
    /**
     * Returns wip-import summary
     * @param jsonResult JSON string
     */
    getSummary(jsonResult) {
        if (jsonResult) {
            const summary = JSON.parse(jsonResult);
            const currencyPipe = new CurrencyPipe(this.locale);
            let summaryMessage = this.translateService.instant(this.summaryKey, {
                total: currencyPipe.transform(summary.Amount, this.currencyCode),
            });
            if (summary.ErrorMessage) {
                summaryMessage += `<br/>${summary.ErrorMessage}`;
            }
            return summaryMessage;
        }
        return null;
    }
    /**
     * The 'type' of migrated data
     */
    get dataType() {
        return this.translateService.instant(this.dataTypeKey);
    }
}
WipSummaryProvider.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: WipSummaryProvider, deps: [{ token: DEFAULT_CURRENCY_CODE }, { token: LOCALE_ID }, { token: i1$1.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
WipSummaryProvider.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: WipSummaryProvider, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: WipSummaryProvider, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [DEFAULT_CURRENCY_CODE]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [LOCALE_ID]
                    }] }, { type: i1$1.TranslateService }];
    } });

class LedgerSummaryProvider {
    constructor(currencyCode, locale, translateService) {
        this.translateService = translateService;
        this.currencyCode = currencyCode;
        this.locale = locale;
    }
    get dataTypeKey() {
        return 'bm.import.status.type.ledger.title';
    }
    get summaryKey() {
        return 'bm.import.status.type.ledger.summary';
    }
    /**
     * Returns ledger-import summary
     * @param jsonResult JSON string
     */
    getSummary(jsonResult) {
        if (jsonResult) {
            const summary = JSON.parse(jsonResult);
            const currencyPipe = new CurrencyPipe(this.locale);
            let summaryMessage = this.translateService.instant(this.summaryKey, {
                invoicesTotal: currencyPipe.transform(summary.invoicesTotal, this.currencyCode),
                receiptsTotal: currencyPipe.transform(summary.receiptsTotal, this.currencyCode),
            });
            if (summary.ErrorMessage) {
                summaryMessage += `<br/>${summary.ErrorMessage}`;
            }
            if (summary.WarningMessage) {
                summaryMessage += `<br/>${summary.WarningMessage}`;
            }
            return summaryMessage;
        }
        return null;
    }
    /**
     * The 'type' of migrated data
     */
    get dataType() {
        return this.translateService.instant(this.dataTypeKey);
    }
}
LedgerSummaryProvider.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LedgerSummaryProvider, deps: [{ token: DEFAULT_CURRENCY_CODE }, { token: LOCALE_ID }, { token: i1$1.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
LedgerSummaryProvider.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LedgerSummaryProvider, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LedgerSummaryProvider, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [DEFAULT_CURRENCY_CODE]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [LOCALE_ID]
                    }] }, { type: i1$1.TranslateService }];
    } });

class ClientSummaryProvider {
    constructor(translateService) {
        this.translateService = translateService;
    }
    get dataTypeKey() {
        return 'bm.import.status.type.clients.title';
    }
    get summaryKey() {
        return 'bm.import.status.type.clients.summary';
    }
    get dataType() {
        return this.translateService.instant(this.dataTypeKey);
    }
    /**
     * Returns summary
     * @param jsonResult JSON string
     */
    getSummary(jsonResult, migrationStatus) {
        if (jsonResult) {
            return this.translateService.instant(this.summaryKey, { numClients: this.getSuccessClients(migrationStatus) });
        }
        return ``;
    }
    getSuccessClients(migrationStatus) {
        var _a;
        return (_a = migrationStatus === null || migrationStatus === void 0 ? void 0 : migrationStatus.SuccessRows) !== null && _a !== void 0 ? _a : 0;
    }
}
ClientSummaryProvider.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ClientSummaryProvider, deps: [{ token: i1$1.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
ClientSummaryProvider.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ClientSummaryProvider, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ClientSummaryProvider, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.TranslateService }]; } });

function addDays(date, days) {
    if (date) {
        date.setDate(date.getDate() + days);
    }
    return date;
}
function today() {
    return new Date();
}

class MigrationResultService {
    constructor(httpClient, translateService) {
        this.httpClient = httpClient;
        this.translateService = translateService;
        this.saveAs = saveAs;
    }
    downloadErrorReport(migrationEventId) {
        return __awaiter(this, void 0, void 0, function* () {
            // Parse Guid from '00000000-0000-0000-0000-000000000000' format to '00000000000000000000000000000000'
            const id = migrationEventId.split('-').join('').toUpperCase();
            const url = `/api/core/v1/migration-result/${id}/failed`;
            const filename = this.translateService.instant('bm.import.status.errorReport.filename');
            yield this.downloadReport(url, filename);
        });
    }
    downloadSuccessfulReport(migrationEventId) {
        return __awaiter(this, void 0, void 0, function* () {
            // Parse Guid from '00000000-0000-0000-0000-000000000000' format to '00000000000000000000000000000000'
            const id = migrationEventId.split('-').join('').toUpperCase();
            const url = `/api/core/v1/migration-result/${id}/completed`;
            const filename = this.translateService.instant('bm.import.status.successfulReport.filename');
            yield this.downloadReport(url, filename);
        });
    }
    downloadReport(url, fileName) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.httpClient.get(url, { responseType: 'blob', observe: 'response' }).toPromise();
            if (response.ok) {
                this.saveAs(response.body, `${fileName}.csv`);
            }
        });
    }
}
MigrationResultService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: MigrationResultService, deps: [{ token: i1.HttpClient }, { token: i1$1.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
MigrationResultService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: MigrationResultService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: MigrationResultService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: i1$1.TranslateService }]; } });

class ImportStatusProgressbarComponent {
}
ImportStatusProgressbarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportStatusProgressbarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ImportStatusProgressbarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: ImportStatusProgressbarComponent, selector: "import-status-progressbar", inputs: { progressCount: "progressCount", progressType: "progressType", status: "status" }, ngImport: i0, template: "<div class=\"importProgressContainer\">\n  <span>{{ status }}</span>\n  <ngb-progressbar class=\"progress-bar-no-transition\" showValue=\"true\" [type]=\"progressType\" [value]=\"progressCount\">\n  </ngb-progressbar>\n</div>\n", styles: [".importProgressContainer div{display:inline-block!important}.importProgressContainer span{float:left!important;width:75px!important}.importProgressContainer ngb-progressbar{float:right;width:calc(100% - 80px)}\n"], components: [{ type: i9.NgbProgressbar, selector: "ngb-progressbar", inputs: ["max", "animated", "striped", "showValue", "textType", "type", "value", "height"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportStatusProgressbarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'import-status-progressbar', template: "<div class=\"importProgressContainer\">\n  <span>{{ status }}</span>\n  <ngb-progressbar class=\"progress-bar-no-transition\" showValue=\"true\" [type]=\"progressType\" [value]=\"progressCount\">\n  </ngb-progressbar>\n</div>\n", styles: [".importProgressContainer div{display:inline-block!important}.importProgressContainer span{float:left!important;width:75px!important}.importProgressContainer ngb-progressbar{float:right;width:calc(100% - 80px)}\n"] }]
        }], propDecorators: { progressCount: [{
                type: Input
            }], progressType: [{
                type: Input
            }], status: [{
                type: Input
            }] } });

class ImportStatusGridComponent {
    constructor(translateService, statusService, migrationResultService) {
        this.translateService = translateService;
        this.statusService = statusService;
        this.migrationResultService = migrationResultService;
        this.isLoading = false;
        this.showPagination = false;
        this.wijmoData = new CollectionView([]);
        this.tooltip = new wijmo.Tooltip();
        this.toolbarData = [];
        this.numberOfDaysToGetImportsFor = -30;
        this.paginationConfig = {
            currentPage: 1,
            itemsPerPage: [25, 50, 100],
        };
        this.toolbarConfig = {
            groupButtonHidden: false,
            groupPanelHidden: true,
            toggleGroupPanel: () => {
                this.toolbarConfig.groupPanelHidden = !this.toolbarConfig.groupPanelHidden;
            },
        };
        this.columnDefinitions = [
            {
                binding: 'fileName',
                header: 'bm.import.status.column.fileName',
                visible: true,
                isReadOnly: true,
            },
            {
                binding: 'data',
                header: 'bm.import.status.column.data',
                visible: true,
                isReadOnly: true,
            },
            {
                binding: 'date',
                header: 'bm.import.status.column.date',
                visible: true,
                isReadOnly: true,
                format: 'g',
            },
            {
                binding: 'uploadedBy',
                header: 'bm.import.status.column.uploadedBy',
                visible: true,
                isReadOnly: true,
            },
            {
                binding: 'status',
                header: 'bm.import.status.column.status',
                visible: true,
                isReadOnly: true,
                hasCustomTemplate: true,
            },
            {
                binding: 'summary',
                header: 'bm.import.status.column.summary',
                visible: true,
                isReadOnly: true,
                hasCustomTemplate: true,
            },
            {
                binding: 'numberOfRowsImported',
                header: 'bm.import.status.column.numberOfRowsImported',
                visible: true,
                isReadOnly: true,
                align: 'right',
                hasCustomTooltip: true,
            },
            {
                binding: 'numberOfFailedRows',
                header: 'bm.import.status.column.numberOfFailedRows',
                visible: true,
                isReadOnly: true,
                align: 'right',
                hasCustomTemplate: true,
                hasCustomTooltip: true,
            },
        ];
    }
    ngOnInit() {
        this.updateToolbarData();
        // Load status data
        this.loadImportStatusesList();
        this.tooltip.cssClass = 'tooltip-info';
    }
    loadImportStatusesList() {
        this.isLoading = true;
        this.showPagination = false;
        this.wijmoData = new CollectionView([]);
        const fromDate = addDays(today(), this.numberOfDaysToGetImportsFor);
        this.statusService
            .getImportStatusesList(fromDate)
            .then(statusData => {
            this.wijmoData = new CollectionView(statusData);
            this.wijmoData.pageSize = this.paginationConfig.itemsPerPage[0];
            this.lastUpdatedDate = new Date();
            this.isLoading = false;
            this.showPagination = this.wijmoData.totalItemCount > this.paginationConfig.itemsPerPage[0];
        })
            .catch(() => {
            this.isLoading = false;
        });
    }
    updateToolbarData() {
        this.toolbarData = [
            {
                label: this.translateService.instant('bm.import.status.grid.toolbar.exportLabel'),
                icon: 'bento-icon-exit',
                action: () => {
                    this.exportToExcel();
                },
            },
        ];
    }
    exportToExcel() {
        wjcGridXlsx.FlexGridXlsxConverter.saveAsync(this.flexGrid, {
            includeColumnHeaders: true,
        }, 'import_progress.xlsx');
    }
    showErrorReportLink(item) {
        return !item.isSuccessful && (item.status === Status.Failed || item.status === Status.Completed);
    }
    downloadErrorReport(item) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.migrationResultService.downloadErrorReport(item.migrationEventId);
        });
    }
    showSuccessfulReportLink(item) {
        return item.numberOfRowsImported > 0;
    }
    downloadSuccessfulReport(item) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.migrationResultService.downloadSuccessfulReport(item.migrationEventId);
        });
    }
    hasCustomTemplate(binding) {
        return this.columnDefinitions.some(c => c.binding === binding && c.hasCustomTemplate);
    }
    wijmoInitialized(flexgrid) {
        flexgrid.hostElement.addEventListener('mousemove', e => {
            let rng = null;
            const ht = this.flexGrid.hitTest(e);
            if (!ht.range.equals(rng)) {
                // New cell selected , show tooltip
                if (ht.cellType === wjcGrid.CellType.Cell) {
                    rng = ht.range;
                    const columnBinding = ht.grid.columns.getColumn(ht.col).binding;
                    const hasCustomTooltip = this.hasCustomTooltip(columnBinding);
                    if (!hasCustomTooltip) {
                        // Check if custom tooltip exist
                        const cellElement = document.elementFromPoint(e.clientX, e.clientY);
                        const cellBounds = this.flexGrid.getCellBoundingRect(ht.row, ht.col);
                        const data = this.flexGrid.getCellData(rng.row, rng.col, true);
                        if (cellElement.clientWidth !== cellElement.scrollWidth) {
                            // Check if text is hidden or not
                            this.tooltip.show(this.flexGrid.hostElement, data, cellBounds); // Show tooltip if text is overflow/hidden
                        }
                    }
                }
            }
        });
        flexgrid.hostElement.addEventListener('mouseout', e => {
            this.tooltip.hide();
        });
    }
    hasCustomTooltip(binding) {
        return this.columnDefinitions.some(c => c.binding === binding && c.hasCustomTooltip);
    }
    onPageChanged(page) {
        this.wijmoData.moveToPage(page - 1);
    }
    onItemsPerPageChanged(pageSize) {
        this.wijmoData.pageSize = pageSize;
    }
}
ImportStatusGridComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportStatusGridComponent, deps: [{ token: i1$1.TranslateService }, { token: ImportStatusService }, { token: MigrationResultService }], target: i0.ɵɵFactoryTarget.Component });
ImportStatusGridComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: ImportStatusGridComponent, selector: "import-status-grid", viewQueries: [{ propertyName: "flexGrid", first: true, predicate: ["flexGrid"], descendants: true, static: true }], ngImport: i0, template: "<div>\n  <h1 id=\"import-statuses-grid-header\" class=\"no-top-margin\">{{ \"bm.import.wizard.status.title\" | translate }}</h1>\n</div>\n<div>\n  <ul class=\"nav navbar-right\">\n    <li>\n      <span>{{ \"bm.import.status.lastUpdated\" | translate }} {{ lastUpdatedDate | date: \"medium\" }}</span>\n      <button class=\"btn btn-default btn-icon btn-lg refresh-button\" (click)=\"loadImportStatusesList()\">\n        <i aria-hidden=\"true\" class=\"bento-icon-refresh\"></i>\n      </button>\n    </li>\n  </ul>\n</div>\n<div>\n  <bento-toolbar class=\"bento-toolbar\" [items]=\"toolbarData\">\n    <ul class=\"nav navbar-right\" role=\"group\">\n      <li id=\"ip-group-panel-button\" *ngIf=\"!toolbarConfig.groupButtonHidden\">\n        <button\n          class=\"btn btn-outline-primary btn-icon btn-toggle\"\n          [ngClass]=\"{ active: !toolbarConfig.groupPanelHidden }\"\n          [attr.aria-pressed]=\"!toolbarConfig.groupPanelHidden\"\n          (click)=\"toolbarConfig.toggleGroupPanel()\"\n          placement=\"bottom\"\n          ngbTooltip=\"{{\n            (toolbarConfig.groupPanelHidden\n              ? 'bm.import.status.grid.grouping.show'\n              : 'bm.import.status.grid.grouping.hide'\n            ) | translate\n          }}\"\n          aria-label=\"toggle group panel\"\n        >\n          <i aria-hidden=\"true\" class=\"bento-icon-tree\"></i>\n        </button>\n      </li>\n      <li *ngIf=\"!toolbarConfig.colVisButtonHidden\">\n        <div ngbDropdown>\n          <button\n            class=\"btn btn-outline-primary btn-icon btn-toggle dropdown-toggle\"\n            id=\"menubutton-icon\"\n            ngbDropdownToggle\n            placement=\"bottom\"\n            [ngbTooltip]=\"'bm.import.status.grid.showHideColumns' | translate\"\n            aria-haspopup=\"true\"\n            aria-controls=\"menu-icon\"\n            aria-label=\"Options\"\n          >\n            <i aria-hidden=\"true\" class=\"bento-icon-cog\"></i>\n            <i aria-hidden=\"true\" class=\"bento-icon-caret-down-filled\"></i>\n          </button>\n          <div ngbDropdownMenu class=\"dropdown-menu grid-dropdown\">\n            <fieldset>\n              <ul class=\"group-list\">\n                <li *ngFor=\"let item of columnDefinitions; index as i\" class=\"dropdown-item\">\n                  <label [attr.for]=\"'column-toolbar-checkbox-' + i\" class=\"grid-toolbar-checkbox-label\">\n                    <bento-checkbox [(ngModel)]=\"item.visible\" [attr.id]=\"'column-toolbar-checkbox-' + i\">\n                    </bento-checkbox>\n                    {{ item.header | translate }}\n                  </label>\n                </li>\n              </ul>\n            </fieldset>\n          </div>\n        </div>\n      </li>\n    </ul>\n  </bento-toolbar>\n  <div class=\"grouping-panel\" *ngIf=\"!toolbarConfig.groupPanelHidden\">\n    <span class=\"grouping-label\"> {{ \"bm.import.status.grid.grouping.label\" | translate }} </span>\n    <wj-group-panel\n      [grid]=\"flexGrid\"\n      [hideGroupedColumns]=\"false\"\n      [placeholder]=\"'bm.import.status.grid.grouping.placeholder' | translate\"\n    >\n    </wj-group-panel>\n  </div>\n  <wj-flex-grid\n    #flexGrid\n    class=\"bento-flex-grid\"\n    [isReadOnly]=\"true\"\n    [headersVisibility]=\"'Column'\"\n    [itemsSource]=\"wijmoData\"\n    [stickyHeaders]=\"true\"\n    [showSort]=\"true\"\n    [selectionMode]=\"0\"\n    [autoRowHeights]=\"true\"\n    (initialized)=\"wijmoInitialized(flexGrid)\"\n  >\n    <wj-flex-grid-column\n      #flexGrid\n      *ngFor=\"let col of columnDefinitions\"\n      [header]=\"col.header | translate\"\n      [binding]=\"col.binding || ''\"\n      [visible]=\"col.visible !== false\"\n      [width]=\"col.width || '*'\"\n      [minWidth]=\"col.minWidth || 100\"\n      [format]=\"col.format || ''\"\n      [align]=\"col.align\"\n    >\n      <ng-template wjFlexGridCellTemplate [cellType]=\"'Cell'\" let-item=\"item\" *ngIf=\"col.binding === 'status'\">\n        <div class=\"d-flex h-100\">\n          <import-status-progressbar\n            [status]=\"item.status\"\n            [progressCount]=\"item.importProgress\"\n            [progressType]=\"item.progressBarType\"\n            class=\"w-100\"\n          >\n          </import-status-progressbar>\n        </div>\n      </ng-template>\n      <ng-template\n        wjFlexGridCellTemplate\n        [cellType]=\"'Cell'\"\n        let-item=\"item\"\n        *ngIf=\"col.binding === 'numberOfFailedRows'\"\n      >\n        <a\n          *ngIf=\"showErrorReportLink(item); else showText\"\n          (click)=\"downloadErrorReport(item)\"\n          [ngbTooltip]=\"'bm.import.status.errorReport.tooltip' | translate\"\n          placement=\"top\"\n          container=\"body\"\n          tooltipClass=\"migration-report-tooltip\"\n          class=\"migration-report-link\"\n        >\n          {{ item.numberOfFailedRows }}\n        </a>\n        <ng-template #showText> {{ item.numberOfFailedRows }} </ng-template>\n      </ng-template>\n      <ng-template\n        wjFlexGridCellTemplate\n        [cellType]=\"'Cell'\"\n        let-item=\"item\"\n        *ngIf=\"col.binding === 'numberOfRowsImported'\"\n      >\n        <a\n          *ngIf=\"showSuccessfulReportLink(item); else showText\"\n          (click)=\"downloadSuccessfulReport(item)\"\n          [ngbTooltip]=\"'bm.import.status.successfulReport.tooltip' | translate\"\n          placement=\"top\"\n          container=\"body\"\n          tooltipClass=\"migration-report-tooltip\"\n          class=\"migration-report-link\"\n        >\n          {{ item.numberOfRowsImported }}\n        </a>\n        <ng-template #showText> {{ item.numberOfRowsImported }} </ng-template>\n      </ng-template>\n      <ng-template wjFlexGridCellTemplate [cellType]=\"'Cell'\" let-item=\"item\" *ngIf=\"col.binding === 'summary'\">\n        <div class=\"text-truncate\" [innerHTML]=\"item.summary\"></div>\n      </ng-template>\n    </wj-flex-grid-column>\n  </wj-flex-grid>\n  <div [bentoBusyLoader]=\"isLoading\" [size]=\"'sm'\"></div>\n  <bento-pagination\n    *ngIf=\"showPagination\"\n    [(page)]=\"paginationConfig.currentPage\"\n    (pageChange)=\"onPageChanged($event)\"\n    (itemsPerPageChange)=\"onItemsPerPageChanged($event)\"\n    [directionLinks]=\"true\"\n    [boundaryLinks]=\"true\"\n    [totalItems]=\"wijmoData.totalItemCount\"\n    [itemsArray]=\"paginationConfig.itemsPerPage\"\n  >\n  </bento-pagination>\n</div>\n", styles: [".bento-flex-grid{max-height:500px}.grouping-panel{border-left:1px solid #d5d4d9;border-right:1px solid #d5d4d9;display:flex;flex-wrap:wrap;padding:0 10px;width:100%}.grouping-label{flex:0 1 auto;line-height:60px}.wj-grouppanel{border-left:none;border-right:none;flex:1 0 auto}.migration-report-link{cursor:pointer}.refresh-button{margin-bottom:10px;margin-left:5px;margin-right:15px}.bento-toolbar.bg-faded{margin-bottom:10px!important}.bento-flex-grid import-status-progressbar{height:16px}h1#import-statuses-grid-header{margin-bottom:-40px;margin-top:0}.navbar-right{float:right!important;margin-right:-15px}\n"], components: [{ type: i4.BentoToolbarComponent, selector: "bento-toolbar", inputs: ["moreText", "items"] }, { type: i4.BentoCheckboxComponent, selector: "bento-checkbox", inputs: ["indeterminate"], outputs: ["indeterminateChange"] }, { type: i5.WjGroupPanel, selector: "wj-group-panel", inputs: ["wjModelProperty", "isDisabled", "tabOrder", "hideGroupedColumns", "showDragGlyphs", "maxGroups", "placeholder", "filter", "groupDescriptionCreator", "grid"], outputs: ["initialized", "gotFocus", "lostFocus", "refreshing", "refreshed", "invalidInput"] }, { type: i6.WjFlexGrid, selector: "wj-flex-grid", inputs: ["wjModelProperty", "isDisabled", "tabOrder", "newRowAtTop", "allowAddNew", "allowDelete", "allowDragging", "allowMerging", "allowResizing", "allowSorting", "allowPinning", "autoScroll", "autoRowHeights", "autoSizeMode", "autoGenerateColumns", "autoSearch", "caseSensitiveSearch", "quickAutoSize", "bigCheckboxes", "childItemsPath", "groupHeaderFormat", "headersVisibility", "showSelectedHeaders", "showMarquee", "showPlaceholders", "itemFormatter", "isReadOnly", "imeEnabled", "mergeManager", "selectionMode", "showGroups", "showSort", "showDropDown", "showAlternatingRows", "showErrors", "alternatingRowStep", "itemValidator", "validateEdits", "treeIndent", "itemsSource", "autoClipboard", "expandSelectionOnCopyPaste", "frozenCells", "frozenRows", "frozenColumns", "cloneFrozenCells", "deferResizing", "sortRowIndex", "editColumnIndex", "stickyHeaders", "preserveSelectedState", "preserveOutlineState", "preserveWhiteSpace", "keyActionTab", "keyActionEnter", "rowHeaderPath", "virtualizationThreshold", "anchorCursor", "lazyRender", "refreshOnEdit", "copyHeaders", "columnGroups", "commitEmptyEdits", "headersFocusability", "ariaLabel", "skipMerged", "pasteEmptyCells", "groupSummaryPosition"], outputs: ["initialized", "gotFocus", "lostFocus", "refreshing", "refreshed", "invalidInput", "beginningEdit", "cellEditEnded", "cellEditEnding", "prepareCellForEdit", "formatItem", "resizingColumn", "resizedColumn", "autoSizingColumn", "autoSizedColumn", "draggingColumn", "draggingColumnOver", "draggedColumn", "sortingColumn", "sortedColumn", "pinningColumn", "pinnedColumn", "resizingRow", "resizedRow", "autoSizingRow", "autoSizedRow", "draggingRow", "draggingRowOver", "draggedRow", "deletingRow", "deletedRow", "loadingRows", "loadedRows", "rowEditStarting", "rowEditStarted", "rowEditEnding", "rowEditEnded", "rowAdded", "groupCollapsedChanging", "groupCollapsedChanged", "columnGroupCollapsedChanging", "columnGroupCollapsedChanged", "itemsSourceChanging", "itemsSourceChanged", "selectionChanging", "selectionChanged", "scrollPositionChanged", "updatingView", "updatedView", "updatingLayout", "updatedLayout", "pasting", "pasted", "pastingCell", "pastedCell", "copying", "copied"] }, { type: i6.WjFlexGridColumn, selector: "wj-flex-grid-column", inputs: ["asyncBindings", "wjProperty", "name", "dataMap", "dataType", "binding", "sortMemberPath", "format", "cellTemplate", "header", "width", "maxLength", "minWidth", "maxWidth", "align", "allowDragging", "allowSorting", "allowResizing", "allowMerging", "aggregate", "isReadOnly", "cssClass", "cssClassAll", "isContentHtml", "isSelected", "visible", "wordWrap", "multiLine", "mask", "inputType", "isRequired", "showDropDown", "dataMapEditor", "dropDownCssClass", "quickAutoSize", "editor"], outputs: ["initialized", "isSelectedChange"] }, { type: ImportStatusProgressbarComponent, selector: "import-status-progressbar", inputs: ["progressCount", "progressType", "status"] }, { type: i4.BentoPaginationComponent, selector: "bento-pagination", inputs: ["page", "totalItems", "itemsPerPage", "goToLink", "hideItemsPerPage", "boundaryLinks", "infoText", "infoPageText", "goToText", "directionLinks", "itemsArray", "pageOverride", "useStrings"], outputs: ["pageChange", "itemsPerPageChange", "eventTracking"] }], directives: [{ type: i4.BentoFocusMonitorDirective, selector: "a, button.btn, button.close, button.dropdown-item, a.dropdown-item, button.bento-number-input-button, button.bento-toast-action-btn" }, { type: i8.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i8.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i4.BentoNgbTooltipSupportDirective, selector: "[ngbTooltip]" }, { type: i9.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "triggers", "container", "disableTooltip", "tooltipClass", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { type: i9.NgbDropdown, selector: "[ngbDropdown]", inputs: ["autoClose", "dropdownClass", "open", "placement", "container", "display"], outputs: ["openChange"], exportAs: ["ngbDropdown"] }, { type: i9.NgbDropdownToggle, selector: "[ngbDropdownToggle]" }, { type: i4.BentoNgbDropdownAnimationDirective, selector: "[ngbDropdownMenu], bento-megamenu" }, { type: i9.NgbDropdownMenu, selector: "[ngbDropdownMenu]" }, { type: i8.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i10.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i10.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { type: i6.WjFlexGridCellTemplate, selector: "[wjFlexGridCellTemplate]", inputs: ["wjFlexGridCellTemplate", "cellOverflow", "valuePaths", "autoSizeRows", "forceFullEdit", "cellType"], exportAs: ["wjFlexGridCellTemplate"] }, { type: i4.BentoBusyLoaderDirective, selector: "[bentoBusyLoader]", inputs: ["bentoBusyLoader", "size", "showDelay", "hideDelay"], outputs: ["show", "hide"] }], pipes: { "translate": i1$1.TranslatePipe, "date": i8.DatePipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportStatusGridComponent, decorators: [{
            type: Component,
            args: [{ selector: 'import-status-grid', template: "<div>\n  <h1 id=\"import-statuses-grid-header\" class=\"no-top-margin\">{{ \"bm.import.wizard.status.title\" | translate }}</h1>\n</div>\n<div>\n  <ul class=\"nav navbar-right\">\n    <li>\n      <span>{{ \"bm.import.status.lastUpdated\" | translate }} {{ lastUpdatedDate | date: \"medium\" }}</span>\n      <button class=\"btn btn-default btn-icon btn-lg refresh-button\" (click)=\"loadImportStatusesList()\">\n        <i aria-hidden=\"true\" class=\"bento-icon-refresh\"></i>\n      </button>\n    </li>\n  </ul>\n</div>\n<div>\n  <bento-toolbar class=\"bento-toolbar\" [items]=\"toolbarData\">\n    <ul class=\"nav navbar-right\" role=\"group\">\n      <li id=\"ip-group-panel-button\" *ngIf=\"!toolbarConfig.groupButtonHidden\">\n        <button\n          class=\"btn btn-outline-primary btn-icon btn-toggle\"\n          [ngClass]=\"{ active: !toolbarConfig.groupPanelHidden }\"\n          [attr.aria-pressed]=\"!toolbarConfig.groupPanelHidden\"\n          (click)=\"toolbarConfig.toggleGroupPanel()\"\n          placement=\"bottom\"\n          ngbTooltip=\"{{\n            (toolbarConfig.groupPanelHidden\n              ? 'bm.import.status.grid.grouping.show'\n              : 'bm.import.status.grid.grouping.hide'\n            ) | translate\n          }}\"\n          aria-label=\"toggle group panel\"\n        >\n          <i aria-hidden=\"true\" class=\"bento-icon-tree\"></i>\n        </button>\n      </li>\n      <li *ngIf=\"!toolbarConfig.colVisButtonHidden\">\n        <div ngbDropdown>\n          <button\n            class=\"btn btn-outline-primary btn-icon btn-toggle dropdown-toggle\"\n            id=\"menubutton-icon\"\n            ngbDropdownToggle\n            placement=\"bottom\"\n            [ngbTooltip]=\"'bm.import.status.grid.showHideColumns' | translate\"\n            aria-haspopup=\"true\"\n            aria-controls=\"menu-icon\"\n            aria-label=\"Options\"\n          >\n            <i aria-hidden=\"true\" class=\"bento-icon-cog\"></i>\n            <i aria-hidden=\"true\" class=\"bento-icon-caret-down-filled\"></i>\n          </button>\n          <div ngbDropdownMenu class=\"dropdown-menu grid-dropdown\">\n            <fieldset>\n              <ul class=\"group-list\">\n                <li *ngFor=\"let item of columnDefinitions; index as i\" class=\"dropdown-item\">\n                  <label [attr.for]=\"'column-toolbar-checkbox-' + i\" class=\"grid-toolbar-checkbox-label\">\n                    <bento-checkbox [(ngModel)]=\"item.visible\" [attr.id]=\"'column-toolbar-checkbox-' + i\">\n                    </bento-checkbox>\n                    {{ item.header | translate }}\n                  </label>\n                </li>\n              </ul>\n            </fieldset>\n          </div>\n        </div>\n      </li>\n    </ul>\n  </bento-toolbar>\n  <div class=\"grouping-panel\" *ngIf=\"!toolbarConfig.groupPanelHidden\">\n    <span class=\"grouping-label\"> {{ \"bm.import.status.grid.grouping.label\" | translate }} </span>\n    <wj-group-panel\n      [grid]=\"flexGrid\"\n      [hideGroupedColumns]=\"false\"\n      [placeholder]=\"'bm.import.status.grid.grouping.placeholder' | translate\"\n    >\n    </wj-group-panel>\n  </div>\n  <wj-flex-grid\n    #flexGrid\n    class=\"bento-flex-grid\"\n    [isReadOnly]=\"true\"\n    [headersVisibility]=\"'Column'\"\n    [itemsSource]=\"wijmoData\"\n    [stickyHeaders]=\"true\"\n    [showSort]=\"true\"\n    [selectionMode]=\"0\"\n    [autoRowHeights]=\"true\"\n    (initialized)=\"wijmoInitialized(flexGrid)\"\n  >\n    <wj-flex-grid-column\n      #flexGrid\n      *ngFor=\"let col of columnDefinitions\"\n      [header]=\"col.header | translate\"\n      [binding]=\"col.binding || ''\"\n      [visible]=\"col.visible !== false\"\n      [width]=\"col.width || '*'\"\n      [minWidth]=\"col.minWidth || 100\"\n      [format]=\"col.format || ''\"\n      [align]=\"col.align\"\n    >\n      <ng-template wjFlexGridCellTemplate [cellType]=\"'Cell'\" let-item=\"item\" *ngIf=\"col.binding === 'status'\">\n        <div class=\"d-flex h-100\">\n          <import-status-progressbar\n            [status]=\"item.status\"\n            [progressCount]=\"item.importProgress\"\n            [progressType]=\"item.progressBarType\"\n            class=\"w-100\"\n          >\n          </import-status-progressbar>\n        </div>\n      </ng-template>\n      <ng-template\n        wjFlexGridCellTemplate\n        [cellType]=\"'Cell'\"\n        let-item=\"item\"\n        *ngIf=\"col.binding === 'numberOfFailedRows'\"\n      >\n        <a\n          *ngIf=\"showErrorReportLink(item); else showText\"\n          (click)=\"downloadErrorReport(item)\"\n          [ngbTooltip]=\"'bm.import.status.errorReport.tooltip' | translate\"\n          placement=\"top\"\n          container=\"body\"\n          tooltipClass=\"migration-report-tooltip\"\n          class=\"migration-report-link\"\n        >\n          {{ item.numberOfFailedRows }}\n        </a>\n        <ng-template #showText> {{ item.numberOfFailedRows }} </ng-template>\n      </ng-template>\n      <ng-template\n        wjFlexGridCellTemplate\n        [cellType]=\"'Cell'\"\n        let-item=\"item\"\n        *ngIf=\"col.binding === 'numberOfRowsImported'\"\n      >\n        <a\n          *ngIf=\"showSuccessfulReportLink(item); else showText\"\n          (click)=\"downloadSuccessfulReport(item)\"\n          [ngbTooltip]=\"'bm.import.status.successfulReport.tooltip' | translate\"\n          placement=\"top\"\n          container=\"body\"\n          tooltipClass=\"migration-report-tooltip\"\n          class=\"migration-report-link\"\n        >\n          {{ item.numberOfRowsImported }}\n        </a>\n        <ng-template #showText> {{ item.numberOfRowsImported }} </ng-template>\n      </ng-template>\n      <ng-template wjFlexGridCellTemplate [cellType]=\"'Cell'\" let-item=\"item\" *ngIf=\"col.binding === 'summary'\">\n        <div class=\"text-truncate\" [innerHTML]=\"item.summary\"></div>\n      </ng-template>\n    </wj-flex-grid-column>\n  </wj-flex-grid>\n  <div [bentoBusyLoader]=\"isLoading\" [size]=\"'sm'\"></div>\n  <bento-pagination\n    *ngIf=\"showPagination\"\n    [(page)]=\"paginationConfig.currentPage\"\n    (pageChange)=\"onPageChanged($event)\"\n    (itemsPerPageChange)=\"onItemsPerPageChanged($event)\"\n    [directionLinks]=\"true\"\n    [boundaryLinks]=\"true\"\n    [totalItems]=\"wijmoData.totalItemCount\"\n    [itemsArray]=\"paginationConfig.itemsPerPage\"\n  >\n  </bento-pagination>\n</div>\n", styles: [".bento-flex-grid{max-height:500px}.grouping-panel{border-left:1px solid #d5d4d9;border-right:1px solid #d5d4d9;display:flex;flex-wrap:wrap;padding:0 10px;width:100%}.grouping-label{flex:0 1 auto;line-height:60px}.wj-grouppanel{border-left:none;border-right:none;flex:1 0 auto}.migration-report-link{cursor:pointer}.refresh-button{margin-bottom:10px;margin-left:5px;margin-right:15px}.bento-toolbar.bg-faded{margin-bottom:10px!important}.bento-flex-grid import-status-progressbar{height:16px}h1#import-statuses-grid-header{margin-bottom:-40px;margin-top:0}.navbar-right{float:right!important;margin-right:-15px}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$1.TranslateService }, { type: ImportStatusService }, { type: MigrationResultService }]; }, propDecorators: { flexGrid: [{
                type: ViewChild,
                args: ['flexGrid', { static: true }]
            }] } });

class ImportStatusComponent {
    constructor(statusService, wipSummaryProvider, ledgerSummaryProvider, clientSummaryProvider) {
        // Register all migration types
        statusService.addMigrationSummaryProvider(MigrationType.TimeAndExpense, wipSummaryProvider);
        statusService.addMigrationSummaryProvider(MigrationType.AccountsReceivable, ledgerSummaryProvider);
        statusService.addMigrationSummaryProvider(MigrationType.Clients, clientSummaryProvider);
    }
}
ImportStatusComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportStatusComponent, deps: [{ token: ImportStatusService }, { token: WipSummaryProvider }, { token: LedgerSummaryProvider }, { token: ClientSummaryProvider }], target: i0.ɵɵFactoryTarget.Component });
ImportStatusComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: ImportStatusComponent, selector: "import-status", ngImport: i0, template: "<div class=\"import-status-container\">\n  <import-status-grid></import-status-grid>\n</div>\n", styles: [".migration-report-tooltip{max-width:100px;opacity:1}.import-status-container{margin-left:auto;margin-right:auto;padding-left:0;padding-right:15px;width:100%}\n"], components: [{ type: ImportStatusGridComponent, selector: "import-status-grid" }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportStatusComponent, decorators: [{
            type: Component,
            args: [{ selector: 'import-status', encapsulation: ViewEncapsulation.None, template: "<div class=\"import-status-container\">\n  <import-status-grid></import-status-grid>\n</div>\n", styles: [".migration-report-tooltip{max-width:100px;opacity:1}.import-status-container{margin-left:auto;margin-right:auto;padding-left:0;padding-right:15px;width:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: ImportStatusService }, { type: WipSummaryProvider }, { type: LedgerSummaryProvider }, { type: ClientSummaryProvider }]; } });

function getCurrentLocale() {
    const locale = cookie('BMLanguageCode');
    return /(en|es|fr|nl|pt).*/.exec(locale) ? locale : 'en-GB';
}
function getCurrencyCode() {
    let code;
    try {
        code = getLocaleCurrencyCode(getCurrentLocale());
    }
    catch (_a) {
        return 'GBP';
    }
    return code;
}

registerLocaleData(localEnGb);
registerLocaleData(localFrCa);
class ImportStatusModule {
}
ImportStatusModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportStatusModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ImportStatusModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportStatusModule, declarations: [ImportStatusComponent, ImportStatusGridComponent, ImportStatusProgressbarComponent], imports: [i1$1.TranslateModule, FormsModule,
        HttpClientModule,
        BentoModule,
        NgbModule,
        CommonModule,
        BrowserAnimationsModule,
        WjGridModule,
        WjGridGrouppanelModule,
        BentoFlexGridControlModule], exports: [ImportStatusComponent] });
ImportStatusModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportStatusModule, providers: [
        {
            provide: LOCALE_ID,
            useFactory: getCurrentLocale,
        },
        {
            provide: DEFAULT_CURRENCY_CODE,
            useFactory: getCurrencyCode,
        },
    ], imports: [[
            TranslateModule.forChild(),
            FormsModule,
            HttpClientModule,
            BentoModule,
            NgbModule,
            CommonModule,
            BrowserAnimationsModule,
            WjGridModule,
            WjGridGrouppanelModule,
            BentoFlexGridControlModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ImportStatusModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ImportStatusComponent, ImportStatusGridComponent, ImportStatusProgressbarComponent],
                    imports: [
                        TranslateModule.forChild(),
                        FormsModule,
                        HttpClientModule,
                        BentoModule,
                        NgbModule,
                        CommonModule,
                        BrowserAnimationsModule,
                        WjGridModule,
                        WjGridGrouppanelModule,
                        BentoFlexGridControlModule,
                    ],
                    exports: [ImportStatusComponent],
                    entryComponents: [ImportStatusComponent],
                    providers: [
                        {
                            provide: LOCALE_ID,
                            useFactory: getCurrentLocale,
                        },
                        {
                            provide: DEFAULT_CURRENCY_CODE,
                            useFactory: getCurrencyCode,
                        },
                    ],
                }]
        }] });

/*
 * Public API Surface of import-status
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ImportStatusComponent, ImportStatusModule };
